import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, signal, computed, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AdminModalComponent } from '../admin-modal/admin-modal.component';

interface FieldRow {
  key: string;
  label: string;
  oldValue: any;
  newValue: any;
  changed: boolean;
}

interface MetaInfo {
  operator: string;
  approver?: string;
  date?: Date;
  status?: string;
  remarks?: string;
  operation?: string;
}

@Component({
  selector: 'app-compare-changes',
  standalone: true,
  imports: [CommonModule, FormsModule, AdminModalComponent],
  template: `
    <app-admin-modal
      [isOpen]="isOpen"
      [title]="readOnly ? 'Verification Details' : 'Compare Changes'"
      [subtitle]="readOnly ? 'Review the project change history' : 'Review the modifications before approval'"
      [icon]="'fa-solid fa-code-compare'"
      [submitText]="'Close'"
      [secondaryText]="''"
      [showFooter]="readOnly"
      [isSubmitting]="isSubmitting"
      (close)="close.emit()"
      (submit)="onPrimary()"
    >
      <div class="cc-container">

        <!-- ── Meta strip ─────────────────────────────────────────── -->
        <div class="cc-meta" *ngIf="meta">
          <div class="cc-meta-item">
            <span class="lbl">Operator</span>
            <span class="val">{{ meta.operator || '—' }}</span>
          </div>
          <div class="cc-meta-item" *ngIf="meta.operation">
            <span class="lbl">Action</span>
            <span class="val pill" [class.pill-create]="meta.operation === 'create'"
                                   [class.pill-update]="meta.operation === 'update'"
                                   [class.pill-delete]="meta.operation === 'delete'">
              {{ meta.operation | uppercase }}
            </span>
          </div>
          <div class="cc-meta-item" *ngIf="meta.date">
            <span class="lbl">Date</span>
            <span class="val">{{ formatDate(meta.date) }}</span>
          </div>
          <div class="cc-meta-item" *ngIf="meta.status">
            <span class="lbl">Status</span>
            <span class="val pill"
                  [class.pill-pending]="meta.status === 'PENDING' || meta.status === 'pending'"
                  [class.pill-approved]="meta.status === 'APPROVED' || meta.status === 'approved'"
                  [class.pill-rejected]="meta.status === 'REJECTED' || meta.status === 'rejected'">
              {{ meta.status }}
            </span>
          </div>
          <div class="cc-meta-item" *ngIf="meta.approver">
            <span class="lbl">Reviewed By</span>
            <span class="val">{{ meta.approver }}</span>
          </div>
        </div>

        <!-- ── Two-column data view ───────────────────────────────── -->
        <div class="cc-mode-banner" *ngIf="isCreate()">
          <i class="fa-solid fa-circle-plus"></i>
          <span>New project creation — fields shown below will be added to the live project record.</span>
        </div>

        <!-- Edit-mode toolbar (approval modal only) -->
        <div class="cc-edit-bar" *ngIf="!readOnly">
          <button type="button" class="cc-edit-toggle" [class.active]="isEditMode()" (click)="toggleEditMode()">
            <i class="fa-solid" [class.fa-pen-to-square]="!isEditMode()" [class.fa-rotate-left]="isEditMode()"></i>
            <span>{{ isEditMode() ? 'Cancel edits' : 'Edit before approval' }}</span>
          </button>
          <span class="cc-edit-hint" *ngIf="isEditMode()">
            Adjust the operator's proposed values — your edits are saved to the live record on approval.
          </span>
        </div>

        <div class="cc-fields">
          <div class="cc-row cc-head">
            <div class="cc-cell-key">Field</div>
            <div class="cc-cell-old">Original</div>
            <div class="cc-cell-new">
              {{ isEditMode() ? 'Final (Editable)' : (isCreate() ? 'New (Proposed)' : 'Updated (Proposed)') }}
            </div>
          </div>

          <div class="cc-row" *ngFor="let f of rows()" [class.changed]="f.changed">
            <div class="cc-cell-key">{{ f.label }}</div>
            <div class="cc-cell-old">
              <span class="muted" *ngIf="isEmpty(f.oldValue); else showOld">—</span>
              <ng-template #showOld>
                <span [class.strike]="f.changed">{{ formatValue(f.oldValue) }}</span>
              </ng-template>
            </div>
            <div class="cc-cell-new">
              <!-- Edit mode: render an input/select bound to the draft for this key -->
              <ng-container *ngIf="isEditMode(); else readonlyNew">
                <select *ngIf="f.key === 'status'"
                        class="cc-input"
                        [ngModel]="drafts()[f.key]"
                        (ngModelChange)="setDraft(f.key, $event)">
                  <option value="Planned">Planned</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Stuck">Stuck</option>
                </select>
                <label *ngIf="f.key === 'hmPriority' || f.key === 'recurringIntervention'" class="cc-checkbox">
                  <input type="checkbox"
                         [checked]="!!drafts()[f.key]"
                         (change)="setDraft(f.key, $any($event.target).checked)">
                  <span>{{ drafts()[f.key] ? 'Yes' : 'No' }}</span>
                </label>
                <input *ngIf="f.key !== 'status' && f.key !== 'hmPriority' && f.key !== 'recurringIntervention'"
                       class="cc-input"
                       [type]="typeof f.newValue === 'number' ? 'number' : 'text'"
                       [ngModel]="drafts()[f.key]"
                       (ngModelChange)="setDraft(f.key, $event)" />
              </ng-container>
              <ng-template #readonlyNew>
                <span class="muted" *ngIf="isEmpty(f.newValue); else showNew">—</span>
                <ng-template #showNew>
                  <span [class.added]="f.changed">{{ formatValue(f.newValue) }}</span>
                </ng-template>
              </ng-template>
            </div>
          </div>

          <div class="cc-empty" *ngIf="rows().length === 0">
            <i class="fa-solid fa-circle-info"></i>
            <p>No data to display.</p>
          </div>
        </div>

        <!-- ── Reviewer comment + remarks ─────────────────────────── -->
        <div class="cc-remarks" *ngIf="!readOnly">
          <label>Reviewer Remarks (Optional)</label>
          <textarea #remarksInput placeholder="Add your feedback for the operator..."></textarea>
          <div class="cc-actions">
            <button type="button" class="btn-reject" (click)="onReject(remarksInput.value)" [disabled]="isSubmitting">
              <i class="fa-solid fa-xmark"></i> Reject
            </button>
            <button type="button" class="btn-approve" (click)="onApprove(remarksInput.value)" [disabled]="isSubmitting">
              <i class="fa-solid fa-check"></i> Approve Now
            </button>
          </div>
        </div>

        <div class="cc-saved-remarks" *ngIf="readOnly && meta?.remarks">
          <span class="lbl"><i class="fa-regular fa-comment-dots"></i> Reviewer Remarks</span>
          <p>{{ meta?.remarks }}</p>
        </div>
      </div>
    </app-admin-modal>
  `,
  styles: [`
    :host { display: block; }
    .cc-container { display: flex; flex-direction: column; gap: 16px; }

    /* Meta strip */
    .cc-meta {
      display: flex; flex-wrap: wrap; gap: 14px;
      padding: 12px 14px; background: #f8fafc;
      border: 1px solid #e2e8f0; border-radius: 10px;
    }
    .cc-meta-item { display: flex; flex-direction: column; gap: 2px; min-width: 120px; }
    .cc-meta-item .lbl { font-size: 10px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.4px; }
    .cc-meta-item .val { font-size: 13px; font-weight: 600; color: #0f172a; }
    .pill { display: inline-block; padding: 2px 9px; border-radius: 9999px; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.4px; }
    .pill-create   { background: #fff7ed; color: #c2410c; }
    .pill-update   { background: #dbeafe; color: #1d4ed8; }
    .pill-delete   { background: #fee2e2; color: #b91c1c; }
    .pill-pending  { background: #fef3c7; color: #b45309; }
    .pill-approved { background: #dcfce7; color: #15803d; }
    .pill-rejected { background: #fee2e2; color: #b91c1c; }

    /* Mode banner */
    .cc-mode-banner {
      display: flex; align-items: center; gap: 10px;
      padding: 10px 14px; border-radius: 10px;
      background: #eff6ff; border: 1px solid #bfdbfe; color: #1d4ed8;
      font-size: 13px; font-weight: 600;
      i { font-size: 14px; }
    }

    /* Fields table */
    .cc-fields {
      border: 1px solid #e2e8f0; border-radius: 10px;
      overflow: hidden; max-height: 50vh; overflow-y: auto;
    }
    .cc-row {
      display: grid;
      grid-template-columns: 180px 1fr 1fr;
      gap: 14px;
      padding: 10px 14px;
      border-bottom: 1px solid #f1f5f9;
      align-items: start;
      font-size: 13px;
    }
    .cc-row:last-child { border-bottom: none; }
    .cc-row.cc-head {
      background: #f8fafc;
      font-size: 11px; font-weight: 800; color: #64748b;
      text-transform: uppercase; letter-spacing: 0.4px;
      position: sticky; top: 0; z-index: 1;
    }
    .cc-cell-key { font-weight: 700; color: #0f172a; }
    .cc-cell-old, .cc-cell-new { color: #1e293b; word-break: break-word; }
    .muted { color: #94a3b8; font-style: italic; }
    .strike { color: #94a3b8; text-decoration: line-through; }
    .added { color: #15803d; font-weight: 700; background: #f0fdf4; padding: 2px 6px; border-radius: 4px; }
    .cc-row.changed { background: #fefce8; }
    .cc-empty { padding: 30px; text-align: center; color: #94a3b8; i { font-size: 24px; display: block; margin-bottom: 8px; } }

    /* Remarks + actions */
    .cc-remarks { display: flex; flex-direction: column; gap: 8px; }
    .cc-remarks label { font-size: 12px; font-weight: 700; color: #475569; }
    .cc-remarks textarea {
      width: 100%; padding: 10px 12px;
      border: 1px solid #e2e8f0; border-radius: 10px;
      font-size: 13px; min-height: 70px; font-family: inherit;
      &:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.10); }
    }
    .cc-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 4px; }
    .cc-actions button {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 8px 16px; border-radius: 9px; border: none;
      font-size: 13px; font-weight: 700; cursor: pointer; transition: background 0.15s;
      &:disabled { opacity: 0.6; cursor: not-allowed; }
    }
    .btn-reject  { background: #fef2f2; color: #b91c1c; &:hover:not(:disabled) { background: #fee2e2; } }
    .btn-approve { background: #1e40af; color: white;   &:hover:not(:disabled) { background: #1d4ed8; } }

    .cc-saved-remarks {
      padding: 12px 14px; background: #f8fafc;
      border: 1px solid #e2e8f0; border-radius: 10px;
      .lbl { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 4px; }
      p { margin: 0; font-size: 13px; color: #1e293b; white-space: pre-wrap; }
    }

    /* ── Edit-mode toolbar + inputs ────────────────────────────── */
    .cc-edit-bar {
      display: flex; align-items: center; gap: 12px;
      flex-wrap: wrap;
      padding: 8px 0;
    }
    .cc-edit-toggle {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 7px 14px;
      border-radius: 8px;
      border: 1px solid #cbd5e1;
      background: white;
      color: #1e40af;
      font-size: 12px;
      font-weight: 700;
      cursor: pointer;
      transition: background 0.15s, transform 0.15s;
      i { font-size: 11px; }
      &:hover { background: #eff6ff; }
      &.active {
        background: #1e40af;
        color: white;
        border-color: #1e40af;
      }
    }
    .cc-edit-hint {
      font-size: 11.5px;
      color: #64748b;
      line-height: 1.4;
      flex: 1;
      min-width: 200px;
    }
    .cc-input {
      width: 100%;
      padding: 6px 10px;
      border: 1px solid #cbd5e1;
      border-radius: 6px;
      font-size: 12.5px;
      font-family: inherit;
      color: #0f172a;
      background: white;
      &:focus { outline: none; border-color: #1e40af; box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.10); }
    }
    .cc-checkbox {
      display: inline-flex; align-items: center; gap: 6px;
      font-size: 12px;
      color: #1e293b;
      cursor: pointer;
      user-select: none;
      input { width: 14px; height: 14px; accent-color: #1e40af; }
    }
  `]
})
export class CompareChangesComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() beforeData: any = null;
  @Input() payload: any = null;        // legacy alias for newData
  @Input() newData: any = null;
  @Input() meta: MetaInfo | null = null;
  @Input() readOnly = false;
  @Input() isSubmitting = false;

  @Output() close   = new EventEmitter<void>();
  /** Approve event. Emits `{ remarks, editedPayload? }`. `editedPayload` is set
   *  only when the approver toggled "Edit before approval" and changed values. */
  @Output() approve = new EventEmitter<{ remarks: string; editedPayload?: Record<string, any> } | string>();
  @Output() reject  = new EventEmitter<string>();

  /** True when the approver toggled the "Edit before approval" mode. */
  isEditMode = signal(false);
  /** Per-field draft values entered by the approver while in edit mode. */
  drafts = signal<Record<string, any>>({});

  /** Triggers a re-render whenever inputs change (signals re-fire the computed below). */
  private tick = signal(0);

  /** Field labels — anything not listed here uses a humanized key. */
  private readonly LABELS: Record<string, string> = {
    name: 'Project Name',
    dept: 'Department',
    loc: 'Location / Block',
    cost: 'Total Cost (₹L)',
    spent: 'Amount Spent (₹L)',
    physical: 'Physical Progress (%)',
    status: 'Status',
    start: 'Start Date',
    end: 'End / Target Date',
    targetDate: 'Target Date',
    scheme: 'Scheme',
    hmPriority: 'HM Priority',
    recurringIntervention: 'Recurring Intervention',
    priority: 'Priority',
    remarks: 'Remarks',
    block: 'Block',
    village: 'Village',
    executorName: 'Executor Name',
    executorNumber: 'Executor Contact',
    visibility: 'Visibility',
    division: 'Division',
    roadLength: 'Road Length (km)',
  };

  /** Keys to skip — internal/system fields. */
  private readonly HIDDEN = new Set([
    'id', 'createdAt', 'updatedAt', 'photoUploadedAt', 'expectedProgress',
    'statusCategory', 'beforeImageUrl', 'currentImageUrl', 'photos', 'photoStatus'
  ]);

  rows = computed<FieldRow[]>(() => {
    this.tick();   // dependency to force re-eval on input change
    const after  = this.newData ?? this.payload ?? {};
    const before = this.beforeData ?? {};
    const all = new Set<string>([...Object.keys(before), ...Object.keys(after)]);
    const result: FieldRow[] = [];
    all.forEach(key => {
      if (this.HIDDEN.has(key)) return;
      const oldV = before?.[key];
      const newV = after?.[key];
      const changed = JSON.stringify(oldV ?? null) !== JSON.stringify(newV ?? null);
      // For new creates, only show keys that have a real value
      const isCreateMode = this.isCreate();
      if (isCreateMode && this.isEmpty(newV)) return;
      result.push({
        key,
        label: this.LABELS[key] || this.humanize(key),
        oldValue: oldV,
        newValue: newV,
        changed,
      });
    });
    // Sort: changed/required fields first, then by label
    result.sort((a, b) => {
      if (a.changed !== b.changed) return a.changed ? -1 : 1;
      return a.label.localeCompare(b.label);
    });
    return result;
  });

  ngOnChanges(_: SimpleChanges): void {
    this.tick.update(t => t + 1);
    // Reset edit-mode state every time a new request is shown
    this.isEditMode.set(false);
    this.drafts.set({});
  }

  toggleEditMode(): void {
    if (this.readOnly) return;
    if (!this.isEditMode()) {
      // Seed drafts with the operator's proposed values so the inputs aren't blank
      const seed: Record<string, any> = {};
      const after = this.newData ?? this.payload ?? {};
      for (const k of Object.keys(after)) {
        if (this.HIDDEN.has(k)) continue;
        seed[k] = after[k];
      }
      this.drafts.set(seed);
    }
    this.isEditMode.update(v => !v);
  }

  /** Update a single draft value (typed by the approver in an edit input). */
  setDraft(key: string, value: any): void {
    this.drafts.update(d => ({ ...d, [key]: value }));
  }

  /** Coerce a draft value back to the same primitive type as the original. */
  private coerce(key: string, raw: any, original: any): any {
    if (raw === undefined || raw === '') return raw;
    if (typeof original === 'number') {
      const n = Number(raw);
      return Number.isFinite(n) ? n : raw;
    }
    if (typeof original === 'boolean') {
      return raw === true || raw === 'true' || raw === 1 || raw === '1';
    }
    return raw;
  }

  /** Build the diff between the operator's values and the approver's edits. */
  private buildEditDiff(): Record<string, any> {
    const after = this.newData ?? this.payload ?? {};
    const diff: Record<string, any> = {};
    const drafts = this.drafts();
    for (const k of Object.keys(drafts)) {
      if (this.HIDDEN.has(k)) continue;
      const coerced = this.coerce(k, drafts[k], after[k]);
      if (JSON.stringify(coerced ?? null) !== JSON.stringify(after[k] ?? null)) {
        diff[k] = coerced;
      }
    }
    return diff;
  }

  isCreate(): boolean {
    if (this.meta?.operation === 'create') return true;
    return !this.beforeData || Object.keys(this.beforeData).length === 0;
  }

  isEmpty(v: any): boolean {
    return v === undefined || v === null || v === '' ||
           (Array.isArray(v) && v.length === 0);
  }

  formatValue(v: any): string {
    if (v === undefined || v === null) return '—';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (Array.isArray(v))      return v.length ? v.join(', ') : '—';
    if (typeof v === 'object') {
      if (typeof v.toDate === 'function') return this.formatDate(v.toDate());
      if (typeof v.seconds === 'number') return this.formatDate(new Date(v.seconds * 1000));
      try { return JSON.stringify(v); } catch { return String(v); }
    }
    return String(v);
  }

  formatDate(d: Date | null | undefined): string {
    if (!d) return '—';
    const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${date}, ${time}`;
  }

  private humanize(key: string): string {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/[_-]/g, ' ')
      .replace(/^\w/, c => c.toUpperCase())
      .trim();
  }

  onPrimary(): void {
    if (this.readOnly) this.close.emit();
    else this.approve.emit('');
  }

  onApprove(remarks: string): void {
    const editedPayload = this.isEditMode() ? this.buildEditDiff() : {};
    const hasEdits = Object.keys(editedPayload).length > 0;
    if (hasEdits) {
      this.approve.emit({ remarks: remarks ?? '', editedPayload });
    } else {
      // Back-compat: when no edits are present, emit the plain remarks string
      this.approve.emit(remarks ?? '');
    }
  }
  onReject(remarks: string): void  { this.reject.emit(remarks ?? ''); }
}
